/**
 * Analytics Service for Dealer Frontend
 * Tracks user actions, bid amounts, payment amounts with local storage and Google Analytics
 */

class AnalyticsService {
  constructor() {
    this.gaId = process.env.REACT_APP_GA_ID || 'G-XXXXXXXXXX';
    this.isInitialized = false;
    this.userInfo = null;
    this.sessionId = this.generateSessionId();
    this.localStorageKey = 'dealer_analytics_events';
    this.maxLocalEvents = 100; // Maximum events to store locally
    this.apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/trustautodev-7ff75/asia-south1';
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `dealer_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Initialize Google Analytics
   */
  init() {
    if (this.isInitialized) return;

    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.gaId}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;

    gtag('js', new Date());
    gtag('config', this.gaId, {
      page_title: document.title,
      page_location: window.location.href,
    });

    this.isInitialized = true;
    console.log('📊 Google Analytics initialized for Dealer Frontend');
  }

  /**
   * Set user information for tracking
   */
  setUser(user) {
    if (!user) return;

    console.log('📊 Setting analytics user:', user);

    this.userInfo = {
      uid: user.uid,
      email: user.email,
      name: user.displayName || user.email,
      roles: user.roles || [],
      organization: user.organization || null,
      dealerId: user.dealerId || null
    };

    // Set user properties in GA
    if (window.gtag) {
      window.gtag('config', this.gaId, {
        user_id: user.uid,
        custom_map: {
          'custom_parameter_1': 'user_roles',
          'custom_parameter_2': 'organization',
          'custom_parameter_3': 'dealer_id'
        }
      });

      // Set user properties
      window.gtag('event', 'user_login', {
        user_id: user.uid,
        user_email: user.email,
        user_roles: JSON.stringify(user.roles || []),
        organization: user.organization || 'unknown',
        dealer_id: user.dealerId || 'unknown'
      });
    }
  }

  /**
   * Store event locally and send to backend
   */
  async storeEvent(eventData) {
    console.log('📊 Storing analytics event:', eventData);
    console.log('📊 Current user info:', this.userInfo);

    // For dealer app, users are always dealer role, so use their name as dealer name
    const dealerName = this.userInfo?.name || this.userInfo?.displayName || this.userInfo?.email;

    const fullEventData = {
      ...eventData,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      pageUrl: window.location.href,
      userAgent: navigator.userAgent,
      userId: this.userInfo?.uid,
      userEmail: this.userInfo?.email,
      userName: this.userInfo?.name,
      dealerName: dealerName,
      userRoles: this.userInfo?.roles || [],
      organization: this.userInfo?.organization,
      dealerId: this.userInfo?.dealerId
    };

    console.log('📊 Full event data:', fullEventData);

    // Detailed logging for gate pass events
    if (eventData.eventName === 'gate_pass_requested') {
      console.log('🚗 Dealer Analytics - Gate Pass Event - Full Event Data:');
      console.log('🚗 Event Name:', fullEventData.eventName);
      console.log('🚗 Event Category:', fullEventData.eventCategory);
      console.log('🚗 Custom Parameters:', JSON.stringify(fullEventData.customParameters, null, 2));
      console.log('🚗 Car Make:', fullEventData.customParameters?.carMake);
      console.log('🚗 Car Model:', fullEventData.customParameters?.carModel);
      console.log('🚗 Registration Number:', fullEventData.customParameters?.registrationNumber);
      console.log('🚗 Dealer Name:', fullEventData.customParameters?.dealerName);
      console.log('🚗 Supplier Name:', fullEventData.customParameters?.supplierName);
      console.log('🚗 Location:', fullEventData.customParameters?.location);
      console.log('🚗 Yard Name:', fullEventData.customParameters?.yardName);
    }

    // Store locally
    this.storeEventLocally(fullEventData);

    // Send to backend
    try {
      await this.sendToBackend(fullEventData);
      console.log('📊 Analytics event sent to backend successfully');
    } catch (error) {
      console.error('Failed to send analytics event to backend:', error);
      // Event is still stored locally, so we can retry later
    }
  }

  /**
   * Send event to backend
   */
  async sendToBackend(eventData) {
    console.log('📊 Sending analytics event to backend:', eventData);
    
    const response = await fetch(`${this.apiBaseUrl}/storeAnalyticsEvent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify(eventData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('📊 Backend error response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const result = await response.json();
    console.log('📊 Backend response:', result);
    return result;
  }

  /**
   * Store event in localStorage
   */
  storeEventLocally(eventData) {
    try {
      const existingEvents = JSON.parse(localStorage.getItem(this.localStorageKey) || '[]');
      existingEvents.push(eventData);

      // Keep only the most recent events
      if (existingEvents.length > this.maxLocalEvents) {
        existingEvents.splice(0, existingEvents.length - this.maxLocalEvents);
      }

      localStorage.setItem(this.localStorageKey, JSON.stringify(existingEvents));
    } catch (error) {
      console.error('Failed to store analytics event locally:', error);
    }
  }

  /**
   * Get locally stored events
   */
  getLocalEvents() {
    try {
      return JSON.parse(localStorage.getItem(this.localStorageKey) || '[]');
    } catch (error) {
      console.error('Failed to retrieve local analytics events:', error);
      return [];
    }
  }

  /**
   * Clear local events
   */
  clearLocalEvents() {
    localStorage.removeItem(this.localStorageKey);
  }

  /**
   * Track page views
   */
  trackPageView(pageName, pagePath = null) {
    console.log('📊 Tracking page view:', pageName, pagePath);
    
    const eventData = {
      eventName: 'page_view',
      eventCategory: 'Navigation',
      eventLabel: pageName,
      pagePath: pagePath || window.location.pathname
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: pageName,
        page_location: pagePath || window.location.href,
        page_path: pagePath || window.location.pathname,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        dealer_id: this.userInfo?.dealerId
      });
    }
  }

  /**
   * Track user actions
   */
  trackAction(action, category = 'User Action', label = null, value = null) {
    const eventData = {
      eventName: action,
      eventCategory: category,
      eventLabel: label,
      eventValue: value
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization,
        dealer_id: this.userInfo?.dealerId
      });
    }
  }

  /**
   * Track bid-related actions
   */
  trackBidAction(action, bidAmount, carId = null, auctionId = null) {
    const eventData = {
      eventName: action,
      eventCategory: 'Bidding',
      eventLabel: `Bid: ${bidAmount}`,
      eventValue: bidAmount,
      customParameters: {
        carId,
        auctionId,
        currency: 'INR'
      }
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', action, {
        event_category: 'Bidding',
        event_label: `Bid: ${bidAmount}`,
        value: bidAmount,
        currency: 'INR',
        car_id: carId,
        auction_id: auctionId,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization,
        dealer_id: this.userInfo?.dealerId
      });
    }
  }

  /**
   * Track Add to My Cars action
   */
  trackAddToMyCars(carData) {
    const {
      carMake,
      carModel,
      carYear,
      dealerName,
      supplierName,
      carId,
      registrationNumber,
      price,
      location
    } = carData;

    const eventData = {
      eventName: 'add_to_my_cars',
      eventCategory: 'Car Management',
      eventLabel: `${carMake} ${carModel} ${carYear}`,
      eventValue: price || 0,
      customParameters: {
        carMake: carMake || 'Unknown',
        carModel: carModel || 'Unknown',
        carYear: carYear || 'Unknown',
        dealerName: dealerName || 'Unknown',
        supplierName: supplierName || 'Unknown',
        carId: carId || 'Unknown',
        registrationNumber: registrationNumber || 'Unknown',
        price: price || 0,
        location: location || 'Unknown',
        city: location || 'Unknown',
        timestamp: new Date().toISOString()
      }
    };

    console.log('📊 Tracking Add to My Cars:', eventData);
    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', 'add_to_my_cars', {
        event_category: 'Car Management',
        event_label: `${carMake} ${carModel} ${carYear}`,
        value: price || 0,
        currency: 'INR',
        car_make: carMake || 'Unknown',
        car_model: carModel || 'Unknown',
        car_year: carYear || 'Unknown',
        dealer_name: dealerName || 'Unknown',
        supplier_name: supplierName || 'Unknown',
        car_id: carId || 'Unknown',
        registration_number: registrationNumber || 'Unknown',
        price: price || 0,
        location: location || 'Unknown',
        dealer_id: this.userInfo?.dealerId
      });
    }
  }

  /**
   * Track Gate Pass Requested action
   */
  trackGatePassRequested(carData) {
    console.log('🚗 Raw car data received (dealer):', carData);
    
    const {
      carMake,
      carModel,
      carYear,
      dealerName,
      supplierName,
      carId,
      registrationNumber,
      price,
      location,
      yardName,
      gatePassType
    } = carData;

    console.log('🚗 Extracted car details (dealer):', {
      carMake,
      carModel,
      carYear,
      dealerName,
      supplierName,
      carId,
      registrationNumber,
      price,
      location,
      yardName,
      gatePassType
    });

    const eventData = {
      eventName: 'gate_pass_requested',
      eventCategory: 'Car Management',
      eventLabel: `Gate Pass: ${carMake} ${carModel} ${carYear}`,
      eventValue: price || 0,
      customParameters: {
        carMake: carMake || 'Unknown',
        carModel: carModel || 'Unknown',
        carYear: carYear || 'Unknown',
        dealerName: dealerName || 'Unknown',
        supplierName: supplierName || 'Unknown',
        carId: carId || 'Unknown',
        registrationNumber: registrationNumber || 'Unknown',
        price: price || 0,
        location: location || 'Unknown',
        city: location || 'Unknown',
        yardName: yardName || 'Unknown',
        gatePassType: gatePassType || 'Standard',
        timestamp: new Date().toISOString()
      }
    };

    console.log('📊 Tracking Gate Pass Requested (dealer):', eventData);
    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', 'gate_pass_requested', {
        event_category: 'Car Management',
        event_label: `Gate Pass: ${carMake} ${carModel} ${carYear}`,
        value: price || 0,
        currency: 'INR',
        car_make: carMake || 'Unknown',
        car_model: carModel || 'Unknown',
        car_year: carYear || 'Unknown',
        dealer_name: dealerName || 'Unknown',
        supplier_name: supplierName || 'Unknown',
        car_id: carId || 'Unknown',
        registration_number: registrationNumber || 'Unknown',
        price: price || 0,
        location: location || 'Unknown',
        yard_name: yardName || 'Unknown',
        gate_pass_type: gatePassType || 'Standard',
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization,
        dealer_id: this.userInfo?.dealerId
      });
    }
  }

  /**
   * Track payment-related actions
   */
  trackPaymentAction(action, paymentAmount, paymentMethod = null, transactionId = null) {
    const eventData = {
      eventName: action,
      eventCategory: 'Payment',
      eventLabel: `Payment: ${paymentAmount}`,
      eventValue: paymentAmount,
      customParameters: {
        paymentMethod,
        transactionId,
        currency: 'INR'
      }
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', action, {
        event_category: 'Payment',
        event_label: `Payment: ${paymentAmount}`,
        value: paymentAmount,
        currency: 'INR',
        payment_method: paymentMethod,
        transaction_id: transactionId,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization,
        dealer_id: this.userInfo?.dealerId
      });
    }
  }

  /**
   * Track car listing actions
   */
  trackCarListingAction(action, carId = null, carDetails = {}) {
    console.log('📊 Tracking car listing action:', action, carId, carDetails);
    
    const eventData = {
      eventName: action,
      eventCategory: 'Car Listing',
      eventLabel: `Car: ${carId}`,
      customParameters: {
        carId,
        ...carDetails
      }
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', action, {
        event_category: 'Car Listing',
        event_label: `Car: ${carId}`,
        car_id: carId,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization,
        dealer_id: this.userInfo?.dealerId
      });
    }
  }

  /**
   * Track auction participation
   */
  trackAuctionAction(action, auctionId = null, bidAmount = null, carId = null) {
    const eventData = {
      eventName: action,
      eventCategory: 'Auction',
      eventLabel: `Auction: ${auctionId}`,
      eventValue: bidAmount,
      customParameters: {
        auctionId,
        carId,
        currency: 'INR'
      }
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', action, {
        event_category: 'Auction',
        event_label: `Auction: ${auctionId}`,
        value: bidAmount,
        currency: 'INR',
        auction_id: auctionId,
        car_id: carId,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization,
        dealer_id: this.userInfo?.dealerId
      });
    }
  }

  /**
   * Track form submissions
   */
  trackFormSubmission(formName, success = true, errorMessage = null) {
    const eventData = {
      eventName: 'form_submission',
      eventCategory: 'Form',
      eventLabel: formName,
      customParameters: {
        success,
        errorMessage
      }
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', 'form_submission', {
        event_category: 'Form',
        event_label: formName,
        success: success,
        error_message: errorMessage,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization,
        dealer_id: this.userInfo?.dealerId
      });
    }
  }

  /**
   * Track file uploads
   */
  trackFileUpload(fileType, fileSize, success = true) {
    const eventData = {
      eventName: 'file_upload',
      eventCategory: 'File Upload',
      eventLabel: fileType,
      eventValue: fileSize,
      customParameters: {
        success
      }
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', 'file_upload', {
        event_category: 'File Upload',
        event_label: fileType,
        file_size: fileSize,
        success: success,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization,
        dealer_id: this.userInfo?.dealerId
      });
    }
  }

  /**
   * Track search actions
   */
  trackSearch(searchTerm, resultsCount = null, category = 'General') {
    const eventData = {
      eventName: 'search',
      eventCategory: 'Search',
      eventLabel: searchTerm,
      customParameters: {
        searchTerm,
        resultsCount,
        category
      }
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', 'search', {
        event_category: 'Search',
        event_label: searchTerm,
        search_term: searchTerm,
        results_count: resultsCount,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization,
        dealer_id: this.userInfo?.dealerId
      });
    }
  }

  /**
   * Track error events
   */
  trackError(errorMessage, errorCode = null, component = null) {
    const eventData = {
      eventName: 'exception',
      eventCategory: 'Error',
      eventLabel: errorMessage,
      customParameters: {
        errorCode,
        component
      }
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: errorMessage,
        fatal: false,
        error_code: errorCode,
        component: component,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization,
        dealer_id: this.userInfo?.dealerId
      });
    }
  }

  /**
   * Track custom business events
   */
  trackBusinessEvent(eventName, parameters = {}) {
    const eventData = {
      eventName,
      eventCategory: 'Business',
      customParameters: parameters
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', eventName, {
        ...parameters,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization,
        dealer_id: this.userInfo?.dealerId
      });
    }
  }

  /**
   * Track user logout
   */
  trackLogout() {
    const eventData = {
      eventName: 'user_logout',
      eventCategory: 'Authentication'
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', 'user_logout', {
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization,
        dealer_id: this.userInfo?.dealerId
      });
    }

    this.userInfo = null;
  }

  /**
   * Get analytics summary from backend
   */
  async getAnalyticsSummary(startDate = null, endDate = null, eventCategory = null) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (eventCategory) params.append('eventCategory', eventCategory);

      const response = await fetch(`${this.apiBaseUrl}/getAnalyticsSummary?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch analytics summary:', error);
      throw error;
    }
  }

  /**
   * Get analytics events from backend
   */
  async getAnalyticsEvents(startDate = null, endDate = null, eventCategory = null, limit = 100, offset = 0) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (eventCategory) params.append('eventCategory', eventCategory);
      params.append('limit', limit);
      params.append('offset', offset);

      const response = await fetch(`${this.apiBaseUrl}/getAnalyticsEvents?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch analytics events:', error);
      throw error;
    }
  }

  /**
   * Track Bid Placement with enhanced user/dealer tracking
   */
  trackBidPlacementEnhanced(bidData, userInfo) {
    const eventData = {
      eventName: 'bid_placement',
      eventCategory: 'Car Management',
      eventLabel: `Bid Placement - ${bidData.carMake} ${bidData.carModel}`,
      eventValue: bidData.bidAmount,
      customParameters: {
        carId: bidData.carId,
        carMake: bidData.carMake,
        carModel: bidData.carModel,
        carYear: bidData.carYear,
        registrationNumber: bidData.registrationNumber,
        city: bidData.location,
        bidAmount: bidData.bidAmount,
        totalBidsPlaced: bidData.totalBidsPlaced,
        maxBids: bidData.maxBids,
        action: 'Bid Placement',
        actionType: 'bid_placement',
        // Enhanced user/dealer tracking
        userId: userInfo.userId,
        userName: userInfo.userName,
        dealerName: userInfo.dealerName,
        userRole: userInfo.userRole,
        isDealerRole: userInfo.isDealerRole,
        organizationId: userInfo.organizationId,
        userEmail: userInfo.userEmail
      }
    };
    
    console.log('📊 Tracking Bid Placement:', eventData);
    this.storeEvent(eventData);
    
    if (window.gtag) {
      window.gtag('event', 'bid_placement', {
        event_category: 'Car Management',
        event_label: `Bid Placement - ${bidData.carMake} ${bidData.carModel}`,
        value: bidData.bidAmount,
        ...eventData.customParameters
      });
    }
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

export default analyticsService;
